/**
 * This workspace's credit balances, and the control that grants it more.
 *
 * THE BALANCES ARE ON THE SAME PANEL AS THE FIELD THAT CHANGES THEM, and that
 * is the single most important thing about this component's layout. A number
 * typed into a box with no current value in view is a number typed blind: the
 * operator has been told "top them up to five thousand" and has no way to know
 * whether that means granting 5,000 or granting 800. Putting the balance one
 * screen away — on a tab, behind a link, in a tooltip — is the same as not
 * showing it, because the person filling in the form will not go and look.
 *
 * A TOP-UP IS STATED IN FULL BEFORE IT IS CONFIRMED. Two steps, not one:
 * "Review" assembles a sentence naming the workspace, the channel, the number of
 * credits, and what the balance will become; "Grant" sends it. The second step
 * is not there to catch a typo in the amount — the review panel is what catches
 * that — it is there because of what the ledger is. `credit_ledger` is
 * append-only, so **there is no undo**. The correction for a wrong top-up is
 * another permanent entry going the other way, which leaves two rows and a
 * conversation, and this screen says so in those words rather than saying
 * "This action cannot be undone", which every screen says and nobody reads.
 *
 * THE IDEMPOTENCY TOKEN IS MINTED ONCE PER TOP-UP INTENT, when this panel
 * mounts, and is re-sent unchanged on every submit of the same form. That is the
 * behaviour `tenantTopupRequestSchema` is designed around: a double-click, a
 * proxy retry or a refresh after a gateway timeout are all the *same* intent and
 * only the client knows it. A fresh token is minted only when the operator
 * explicitly starts another top-up. The visible consequence is `applied: false`
 * — the server recognised the key, moved nothing, and handed back the entry the
 * first attempt created. This panel reports that as its own outcome, in its own
 * words, rather than as a second success: a screen that cannot tell the two
 * apart is a screen that will cheerfully report a grant that never happened.
 *
 * KEEPING THE TOKEN IS WHY THE CHANNEL AND THE AMOUNT STAY EDITABLE AFTER A
 * FAILURE, and that pairing is only safe because the API refuses the crossing.
 * A submit that fails returns to `step: 'editing'` with the token intact and
 * both fields live, so "the response was lost, so I changed the channel and
 * pressed it again" is an ordinary thing to do here. The server answers that
 * with a `409` naming the movement the token is already spoken for, which
 * arrives as an ordinary form error; it does **not** answer `applied: false`,
 * which this panel would render as "nothing moved — this top-up had already been
 * recorded" about a grant on a channel that was never credited. The way forward
 * the error names is "Start another top-up", which mints a fresh token.
 *
 * NOTHING HERE IS MONEY. No currency symbol, no "spend", no "charge", no
 * "refund". A credit is Wend's own metering unit; under bring-your-own-SES the
 * tenant pays Amazon directly for what happens on their own account, in amounts
 * Wend never sees. `lib/credit-display.ts` holds that line for both apps.
 *
 * CREDITS BELONG TO CONNECT. Since slice 65 split the old Reach, only Connect's
 * email and WhatsApp sends spend a credit; Reach's Meta ads are paid for on the
 * workspace's own ad account and Sense sends nothing. A workspace without
 * Connect therefore has nothing that would spend what this panel grants, and
 * the panel says so in one line. It does not refuse the top-up: entitlement is
 * advisory everywhere else (no API route checks it), a grant made ahead of an
 * upgrade is a reasonable thing for staff to do, and a panel that hid itself
 * would also hide a balance the workspace still holds from before a downgrade.
 *
 * THE FIGURES ARE METRIC PANELS (slice 66), and none has a meter. Each channel's
 * balance, reserved and available are `@wend/ui`'s `MetricPanel` at the `md`
 * size, in sans tabular numerals rather than the mono they were drawn in —
 * mono's dotted zero reads as an 8 at a glance, which is the misreading this
 * panel can least afford. They are in the tenant's own Credits page's order and
 * emphasis — Available first and violet, then Balance and Reserved plain —
 * because staff and the customer look at the same three numbers about the same
 * workspace, and a support call in which each side's headline figure is a
 * different number goes badly. The first slice 66 cut washed Balance here (the
 * figure a top-up moves) while the web page washed Available (the figure a
 * campaign is blocked on); the review made them one. **No meter on
 * any of them**: a meter draws a share of a maximum, and a balance has no
 * maximum — a bar would invent one. A channel that has never held a credit
 * still gets its sentence instead of three zeros; MetricPanel's "measured"
 * branch is only reached for a channel with a ledger.
 */
import { Coins, RotateCw } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';

import {
  MAX_TOPUP_REASON_LENGTH,
  availableCredits,
  tenantTopupRequestSchema,
  type Channel,
  type CreditBalance,
  type Tenant,
  type TenantTopupResponse,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Kicker,
  MetricPanel,
  Select,
  Spinner,
} from '@wend/ui';

import { FormAlert } from '../../components/FormAlert';
import { describeApiError, toApiError, type ApiError } from '../../lib/api-error';
import { CHANNELS, CHANNEL_CREDIT_UNIT, CHANNEL_LABEL } from '../../lib/credit-display';
import { fetchTenantBalances, topUpTenantCredits } from '../../lib/credits-api';
import { formatCount, formatDateTime } from '../../lib/format';
import {
  NO_FIELD_ERRORS,
  toFieldErrorMap,
  zodFieldErrors,
  type FieldErrorMap,
} from '../../lib/form';
import { useAsyncData } from '../../lib/use-async-data';
import { ReasonField } from './ReasonField';

type TopupStep = 'editing' | 'confirming';

export interface TenantCreditsPanelProps {
  tenant: Tenant;
}

export function TenantCreditsPanel({ tenant }: TenantCreditsPanelProps) {
  const { status, data, error, refreshing, reload } = useAsyncData(
    (signal) => fetchTenantBalances(tenant.id, signal),
    [tenant.id],
  );

  const balances = data?.balances ?? [];
  const isFirstLoad = status === 'loading' && data === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1.02rem]">Credits</CardTitle>
        <CardDescription>
          Credits are Wend&rsquo;s own metering unit — how much of this workspace&rsquo;s plan has
          been used. They are not money: this workspace&rsquo;s messages leave its own Amazon SES
          account, and Amazon bills the customer directly for what it actually does.
        </CardDescription>
        {!tenant.features.includes('connect') && (
          <p className="text-[0.85rem] leading-snug text-muted">
            Credits are spent only by Connect sends; this plan has no Connect.
          </p>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {isFirstLoad && (
          <div className="flex items-center gap-3 py-6 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading this workspace&rsquo;s balances…</span>
          </div>
        )}

        {!isFirstLoad && (
          <>
            {/*
              A failed read does NOT remove the ability to grant credits. A
              customer who has paid should not be blocked by a read endpoint
              being down, and the ledger write does not depend on this figure.
              What it does do is say, here and again in the confirmation, that
              the number is unknown — because the whole argument for putting the
              balances on this panel is that a number typed without the current
              one in view is typed blind, and pretending otherwise would be
              worse than the blocking we are choosing not to do.
            */}
            {error !== null && (
              <FormAlert requestId={error.requestId}>
                {data === null
                  ? `${describeApiError(error)} This workspace's balances could not be read at all, so nothing below states what it holds. A top-up will still be recorded correctly — but you would be choosing the amount without the current one in view.`
                  : `${describeApiError(error)} The balances below are the last version that loaded — re-read them before granting anything from these figures.`}
              </FormAlert>
            )}

            {data !== null && (
              <div className="flex flex-col gap-5">
                {balances.map((balance) => (
                  <ChannelBalance key={balance.channel} balance={balance} />
                ))}
                {refreshing && <Spinner size="sm" label="Re-reading the balances" />}
              </div>
            )}

            {error !== null && (
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={refreshing}
                  leadingIcon={<RotateCw aria-hidden="true" />}
                  onClick={reload}
                >
                  Reload balances
                </Button>
              </div>
            )}

            <TopupForm
              tenant={tenant}
              balances={balances}
              stale={status === 'error'}
              onFinished={() => {
                // The balances are re-read either way. A replay moved nothing
                // *now*, but the top-up it matched did move something at some
                // point, and the figures on this panel were read before that —
                // leaving them alone would put two different numbers for one
                // channel on the same screen.
                //
                // Nothing else on the screen is re-read, and that is a
                // correction rather than an omission. This used to bump the
                // change-history panel's reload token when `applied` was true,
                // which made an unchanged list visibly refresh as though a row
                // had just landed in it — a grant writes no `admin_audit` row,
                // so no re-read of that list can ever show one. See
                // `TenantAuditPanel`'s header.
                reload();
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Balances                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One channel.
 *
 * `updatedAt === null` gets its own sentence rather than three zeros, because
 * "this workspace has never had a credit on this channel" and "the balance is
 * zero" are different statements and only one of them is ever a surprise. The
 * same distinction the rest of this codebase draws between a rate of zero and a
 * rate that was never measured.
 */
function ChannelBalance({ balance }: { balance: CreditBalance }) {
  const label = CHANNEL_LABEL[balance.channel];

  if (balance.updatedAt === null) {
    return (
      <div className="border-t border-line pt-5 first:border-t-0 first:pt-0">
        <Kicker>{label}</Kicker>
        <p className="mt-2 max-w-prose text-[0.9rem] leading-relaxed text-muted">
          This workspace has never held a {label} credit. That is not the same as a balance of
          zero — no credit has ever been granted, consumed or released on this channel.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-line pt-5 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Kicker tone="brand">{label}</Kicker>
        <Kicker>last moved {formatDateTime(balance.updatedAt)}</Kicker>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <MetricPanel
          kicker="Available"
          figureSize="md"
          figure={{ kind: 'measured', value: availableCredits(balance) }}
          sub="Balance minus reserved — what can still be sent"
        />
        <MetricPanel
          variant="plain"
          kicker="Balance"
          figureSize="md"
          figure={{ kind: 'measured', value: balance.balance }}
          sub="Credits this workspace holds"
        />
        <MetricPanel
          variant="plain"
          kicker="Reserved"
          figureSize="md"
          figure={{ kind: 'measured', value: balance.reserved }}
          sub="Held by messages in flight — not yet delivered or resolved"
        />
      </div>

      <p className="mt-3 text-[0.8rem] leading-snug text-muted">
        {CHANNEL_CREDIT_UNIT[balance.channel]}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Top-up                                                                      */
/* -------------------------------------------------------------------------- */

function TopupForm({
  tenant,
  balances,
  stale,
  onFinished,
}: {
  tenant: Tenant;
  balances: readonly CreditBalance[];
  /** True when the balances on screen failed to re-read and may be behind. */
  stale: boolean;
  /**
   * Called once the API has answered, whether or not anything moved.
   *
   * It carries no `applied` flag any more. It used to, so the caller could
   * decide whether to re-read the change-history panel as well — but a grant
   * writes no `admin_audit` row, so that list cannot change either way, and
   * re-reading it presented an unchanged list as though something had landed in
   * it. What this panel needs re-read after any answer is its own balances.
   */
  onFinished: () => void;
}) {
  /**
   * Minted once, when this panel mounts, and re-sent unchanged on every submit
   * of this form. See the file header — this is the whole idempotency story,
   * and re-minting it per submit would turn a retry into a second grant.
   */
  const [requestToken, setRequestToken] = useState<string>(() => crypto.randomUUID());

  const [channel, setChannel] = useState<Channel>('email');
  const [amountInput, setAmountInput] = useState('');
  const [reason, setReason] = useState('');
  const [step, setStep] = useState<TopupStep>('editing');
  const [errors, setErrors] = useState<FieldErrorMap>(NO_FIELD_ERRORS);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [localMessage, setLocalMessage] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);
  const [result, setResult] = useState<TenantTopupResponse | null>(null);

  const currentBalance = useMemo(
    () => balances.find((entry) => entry.channel === channel) ?? null,
    [balances, channel],
  );

  const channelOptions = useMemo(
    () =>
      CHANNELS.map((value) => ({
        value,
        label: CHANNEL_LABEL[value],
        description: CHANNEL_CREDIT_UNIT[value],
      })),
    [],
  );

  /** The body, before `tenantTopupRequestSchema` has had its say. */
  const buildRequest = (): unknown => ({
    channel,
    // `''` becomes NaN rather than 0: an empty box is not a top-up of nothing,
    // and `Number('')` being 0 is how an empty field passes a `>= 0` check.
    amount: amountInput.trim() === '' ? Number.NaN : Number(amountInput),
    reason,
    requestToken,
  });

  const review = (): void => {
    setFailure(null);
    setLocalMessage(null);

    const parsed = tenantTopupRequestSchema.safeParse(buildRequest());
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      setLocalMessage('Check the highlighted fields before reviewing this top-up.');
      return;
    }

    setErrors(NO_FIELD_ERRORS);
    setStep('confirming');
  };

  const grant = async (): Promise<void> => {
    setFailure(null);
    setLocalMessage(null);

    const parsed = tenantTopupRequestSchema.safeParse(buildRequest());
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      setStep('editing');
      setLocalMessage('Check the highlighted fields before reviewing this top-up.');
      return;
    }

    setGranting(true);
    try {
      const response = await topUpTenantCredits(tenant.id, parsed.data);
      setResult(response);
      setStep('editing');
      onFinished();
    } catch (error: unknown) {
      const apiError = toApiError(error);
      setFailure(apiError);
      setErrors(toFieldErrorMap(apiError.fieldErrors));
      setStep('editing');
    } finally {
      setGranting(false);
    }
  };

  /** A new intent: a new token, and an empty form. */
  const startAnother = (): void => {
    setRequestToken(crypto.randomUUID());
    setAmountInput('');
    setReason('');
    setErrors(NO_FIELD_ERRORS);
    setFailure(null);
    setLocalMessage(null);
    setResult(null);
    setStep('editing');
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (step === 'editing') review();
    else void grant();
  };

  if (result !== null) {
    return <TopupReceipt tenant={tenant} result={result} onStartAnother={startAnother} />;
  }

  const amount = Number(amountInput);
  const amountIsUsable = amountInput.trim() !== '' && Number.isInteger(amount) && amount > 0;

  // `bad`, not `warn`, when the current balance is unknown: that is the one case
  // where the confirmation cannot say what the top-up will produce, and a tone
  // matching every other confirmation would let it read as an ordinary one.
  const confirmTone = currentBalance === null ? 'bad' : 'warn';

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5 border-t border-line pt-6">
      <div>
        <h3 className="text-[1rem] font-medium leading-snug">Grant credits</h3>
        <p className="mt-1 max-w-prose text-[0.88rem] leading-relaxed text-muted">
          A top-up is written to this workspace&rsquo;s credit ledger as a new entry. The ledger is
          append-only, so a top-up cannot be deleted or edited afterwards — a mistake is corrected
          by a second, equally permanent entry going the other way.
        </p>
      </div>

      {failure !== null && (
        <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>
      )}
      {failure === null && localMessage !== null && <FormAlert>{localMessage}</FormAlert>}

      <div className="grid gap-5 sm:grid-cols-2">
        <FormField
          label="Channel"
          required
          error={errors.channel}
          description="Email and WhatsApp credits are separate balances. A grant to one does nothing for the other."
        >
          <Select
            value={channel}
            options={channelOptions}
            disabled={step === 'confirming'}
            onValueChange={(value) => {
              setChannel(value as Channel);
            }}
          />
        </FormField>

        <FormField
          label="Credits to add"
          required
          error={errors.amount}
          description={
            currentBalance === null
              ? 'A whole number of credits.'
              : `Adds to the current balance of ${formatCount(currentBalance.balance)}. It does not set it.`
          }
        >
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={amountInput}
            disabled={step === 'confirming'}
            className="tabular-nums"
            onChange={(event) => {
              setAmountInput(event.target.value);
            }}
          />
        </FormField>
      </div>

      {/*
        `MAX_TOPUP_REASON_LENGTH` — 256 — and NOT `MAX_ADMIN_REASON_LENGTH`. A
        top-up's reason is recorded in `audit_log.metadata`, which refuses a
        longer value rather than truncating it; the edit form above stores its
        reason in a 500-character column. The counter under this box has to state
        the cap this form is actually held to, or the operator meets it as a 400
        after the review step.
      */}
      <ReasonField
        value={reason}
        onChange={setReason}
        error={errors.reason}
        disabled={step === 'confirming'}
        maxLength={MAX_TOPUP_REASON_LENGTH}
        explains="somebody asking why this workspace's balance is what it is can see what was agreed and where — a paid invoice number, the ticket that authorised it, the goodwill credit and who approved it."
      />

      {step === 'confirming' && amountIsUsable && (
        <Callout
          tone={confirmTone}
          live="polite"
          title={`Grant ${formatCount(amount)} ${CHANNEL_LABEL[channel]} credits to ${tenant.name}?`}
        >
          <p>
            {currentBalance === null ? (
              <>
                This workspace&rsquo;s {CHANNEL_LABEL[channel]} balance could not be read, so what
                it will become cannot be stated here — you are choosing this amount without seeing
                the current one. The grant itself will still be recorded correctly.
              </>
            ) : (
              <>
                Its {CHANNEL_LABEL[channel]} balance goes from{' '}
                <span className="font-mono tabular-nums text-text">
                  {formatCount(currentBalance.balance)}
                </span>{' '}
                to{' '}
                <span className="font-mono tabular-nums text-text">
                  {formatCount(currentBalance.balance + amount)}
                </span>
                .
              </>
            )}
          </p>

          <p className="mt-2">
            This writes one permanent entry to the credit ledger, attributed to you, with the reason
            above. <strong className="font-medium text-text">Pressing it twice does nothing
            twice</strong> — the second press is recognised as the same top-up and moves nothing —
            but there is no undo: if the amount is wrong, correcting it means a second permanent
            entry, and both will show in this workspace&rsquo;s history for good.
          </p>

          {stale && (
            <p className="mt-2 text-warn">
              The balances above failed to re-read, so the figures in this sentence may be out of
              date. Reload before granting.
            </p>
          )}
        </Callout>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {step === 'editing' ? (
          <Button type="submit" leadingIcon={<Coins aria-hidden="true" />}>
            Review this top-up
          </Button>
        ) : (
          <>
            <Button type="submit" loading={granting}>
              {amountIsUsable
                ? `Grant ${formatCount(amount)} ${CHANNEL_LABEL[channel]} credits`
                : 'Grant credits'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={granting}
              onClick={() => {
                setStep('editing');
              }}
            >
              Back
            </Button>
          </>
        )}
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* What the top-up did                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The outcome, told apart by `applied`.
 *
 * `applied: false` is not a failure and is not a success either — it is "the
 * server had already seen this exact top-up and did nothing". It gets its own
 * tone, its own heading and its own way out, because the operator's next
 * question ("so did they get the credits or not?") has a precise answer and a
 * generic green tick does not give it.
 */
function TopupReceipt({
  tenant,
  result,
  onStartAnother,
}: {
  tenant: Tenant;
  result: TenantTopupResponse;
  onStartAnother: () => void;
}) {
  const label = CHANNEL_LABEL[result.balance.channel];

  return (
    <div className="flex flex-col gap-4 border-t border-line pt-6">
      {/*
        `assertive` for the replay, deliberately. A grant that landed is good
        news and can wait its turn; "nothing moved" is the one an operator must
        not walk away from believing the credits arrived.
      */}
      <Callout
        tone={result.applied ? 'good' : 'info'}
        live={result.applied ? 'polite' : 'assertive'}
        title={
          result.applied
            ? `${formatCount(result.amount)} ${label} credits granted to ${tenant.name}`
            : 'Nothing moved — this top-up had already been recorded'
        }
      >
        {result.applied ? (
          <p>
            One entry was appended to this workspace&rsquo;s credit ledger, attributed to your staff
            account with the reason you gave. Its {label} balance is now{' '}
            <span className="font-mono tabular-nums text-text">
              {formatCount(result.balance.balance)}
            </span>
            .{' '}
            {/*
              Said here because the panel below cannot say it. A grant is
              audited into `audit_log`, not `admin_audit`, so the change-history
              panel at the bottom of this screen will not list it — and an
              operator who checks there and finds nothing would reasonably
              conclude this receipt was wrong. The entry id above is what a
              support conversation is actually conducted with.
            */}
            The change history at the bottom of this screen covers detail corrections only, so this
            grant will not appear in it — quote the ledger entry below instead.
          </p>
        ) : (
          <p>
            The API recognised this as the same top-up you already submitted, so it appended
            nothing. The balance below is the one that first top-up produced — not a second grant.
            If you meant to add more credits on top, start a new top-up.
          </p>
        )}

        <dl className="mt-3 flex flex-col gap-1.5 font-mono text-[0.72rem] tracking-[0.04em]">
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted">{label} balance</dt>
            <dd className="tabular-nums text-text">{formatCount(result.balance.balance)}</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted">Ledger entry</dt>
            <dd className="break-all text-muted">{result.ledgerEntryId}</dd>
          </div>
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted">Idempotency key</dt>
            <dd className="break-all text-muted">{result.idempotencyKey}</dd>
          </div>
        </dl>
      </Callout>

      <div>
        <Button
          type="button"
          variant="secondary"
          leadingIcon={<RotateCw aria-hidden="true" />}
          onClick={onStartAnother}
        >
          Start another top-up
        </Button>
      </div>
    </div>
  );
}
